import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import supabase from '../../lib/supabase'
import SafeIcon from '../../common/SafeIcon'
import Modal from '../common/Modal'
import * as FiIcons from 'react-icons/fi'

const emptyManualLead = () => ({ name: '', email: '', phone: '', subject: '', message: '' })
const emptyClientForm = () => ({ client_name: '', email: '', phone: '', project_title: '' })

export default function LeadsManager() {
  const navigate = useNavigate()
  const [leads, setLeads] = useState([])
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [manualLead, setManualLead] = useState(emptyManualLead())
  const [showManualForm, setShowManualForm] = useState(false)
  const [currentLead, setCurrentLead] = useState(null)
  const [showClientModal, setShowClientModal] = useState(false)
  const [clientForm, setClientForm] = useState(emptyClientForm())

  useEffect(() => { loadLeads() }, [])

  async function loadLeads() {
    try {
      setLoading(true)
      setError('')
      const { data, error: rpcError } = await supabase.rpc('codevaro_leads_workflow_list')
      if (rpcError) throw rpcError
      setLeads(data || [])
    } catch (err) {
      console.error(err)
      setError(`Σφάλμα κατά τη φόρτωση των leads: ${err.message}`)
    } finally {
      setLoading(false)
    }
  }

  const filteredLeads = useMemo(() => {
    const term = searchTerm.trim().toLowerCase()
    return leads.filter((lead) => {
      const matchesStatus = statusFilter === 'all' || lead.status === statusFilter
      const haystack = [lead.name, lead.email, lead.phone, lead.subject, lead.client_name, lead.internal_project_title]
        .filter(Boolean).join(' ').toLowerCase()
      return matchesStatus && (!term || haystack.includes(term))
    })
  }, [leads, searchTerm, statusFilter])

  async function createManualLead(e) {
    e.preventDefault()
    try {
      setSubmitting(true)
      setError('')
      setSuccess('')
      const { error: rpcError } = await supabase.rpc('codevaro_admin_lead_create', {
        p_name: manualLead.name,
        p_email: manualLead.email || null,
        p_phone: manualLead.phone || null,
        p_subject: manualLead.subject || null,
        p_message: manualLead.message || null
      })
      if (rpcError) throw rpcError
      setManualLead(emptyManualLead())
      setShowManualForm(false)
      setSuccess('Το lead προστέθηκε χειροκίνητα.')
      await loadLeads()
    } catch (err) {
      console.error(err)
      setError(`Σφάλμα δημιουργίας lead: ${err.message}`)
    } finally {
      setSubmitting(false)
    }
  }

  async function createProject(lead) {
    try {
      setSubmitting(true)
      setError('')
      setSuccess('')
      const { data, error: rpcError } = await supabase.rpc('codevaro_internal_project_create_from_lead', {
        p_lead_id: lead.id,
        p_title: `Έργο για ${lead.name || 'Lead'}`,
        p_description: lead.message || null,
        p_status: 'planning',
        p_priority: 'medium'
      })
      if (rpcError) throw rpcError
      const projectId = data?.[0]?.project_id
      setSuccess('Το project δημιουργήθηκε. Μπορείτε τώρα να καταχωρήσετε έσοδα και έξοδα.')
      await loadLeads()
      if (projectId) navigate('/admin/project-finance', { state: { projectId } })
    } catch (err) {
      console.error(err)
      setError(`Σφάλμα δημιουργίας project: ${err.message}`)
    } finally {
      setSubmitting(false)
    }
  }

  function openClientConversion(lead) {
    setCurrentLead(lead)
    setClientForm({
      client_name: lead.name || '',
      email: lead.email || '',
      phone: lead.phone || '',
      project_title: lead.internal_project_title || `Έργο για ${lead.name || 'Πελάτη'}`
    })
    setShowClientModal(true)
  }

  async function convertToClient(e) {
    e.preventDefault()
    if (!currentLead) return
    try {
      setSubmitting(true)
      setError('')
      const { error: rpcError } = await supabase.rpc('codevaro_lead_convert_to_new_client', {
        p_lead_id: currentLead.id,
        p_client_name: clientForm.client_name,
        p_legal_name: null,
        p_email: clientForm.email || null,
        p_phone: clientForm.phone || null,
        p_website: null,
        p_vat_number: null,
        p_address: null,
        p_city: null,
        p_country: null,
        p_notes: null,
        p_create_contact: true,
        p_contact_position: null,
        p_create_project: !currentLead.internal_project_id,
        p_project_title: clientForm.project_title || null
      })
      if (rpcError) throw rpcError
      setShowClientModal(false)
      setCurrentLead(null)
      setSuccess('Το lead μετατράπηκε σε πελάτη χωρίς να χαθεί η σύνδεση με το project.')
      await loadLeads()
    } catch (err) {
      console.error(err)
      setError(`Σφάλμα μετατροπής: ${err.message}`)
    } finally {
      setSubmitting(false)
    }
  }

  function openProjectFinance(lead) {
    if (!lead.internal_project_id) return
    navigate('/admin/project-finance', { state: { projectId: lead.internal_project_id } })
  }

  function statusBadgeClasses(status) {
    if (status === 'converted') return 'bg-green-100 text-green-800'
    if (status === 'new') return 'bg-blue-100 text-blue-800'
    if (status === 'contacted') return 'bg-yellow-100 text-yellow-800'
    return 'bg-gray-100 text-gray-800'
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Leads</h1>
          <p className="mt-1 text-sm text-gray-600">Όλα τα leads — από φόρμες ή χειροκίνητα — μπορούν να γίνουν project άμεσα. Η δημιουργία πελάτη είναι προαιρετικό επόμενο βήμα.</p>
        </div>
        <button onClick={() => setShowManualForm((v) => !v)} className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700">
          <SafeIcon icon={FiIcons.FiPlus} className="h-4 w-4" /> Νέο Lead
        </button>
      </div>

      {error && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700">{error}</div>}
      {success && <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-green-700">{success}</div>}

      {showManualForm && (
        <form onSubmit={createManualLead} className="rounded-xl bg-white p-5 shadow space-y-4">
          <h2 className="font-semibold text-gray-900">Χειροκίνητη προσθήκη lead</h2>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <input required placeholder="Όνομα *" value={manualLead.name} onChange={(e) => setManualLead((p) => ({ ...p, name: e.target.value }))} className="rounded-lg border border-gray-300 px-3 py-2" />
            <input type="email" placeholder="Email" value={manualLead.email} onChange={(e) => setManualLead((p) => ({ ...p, email: e.target.value }))} className="rounded-lg border border-gray-300 px-3 py-2" />
            <input placeholder="Τηλέφωνο" value={manualLead.phone} onChange={(e) => setManualLead((p) => ({ ...p, phone: e.target.value }))} className="rounded-lg border border-gray-300 px-3 py-2" />
            <input placeholder="Θέμα" value={manualLead.subject} onChange={(e) => setManualLead((p) => ({ ...p, subject: e.target.value }))} className="rounded-lg border border-gray-300 px-3 py-2" />
          </div>
          <textarea rows={3} placeholder="Σημειώσεις / αίτημα" value={manualLead.message} onChange={(e) => setManualLead((p) => ({ ...p, message: e.target.value }))} className="w-full rounded-lg border border-gray-300 px-3 py-2" />
          <div className="flex gap-2"><button type="submit" disabled={submitting} className="rounded-lg bg-blue-600 px-4 py-2 text-white disabled:opacity-50">Αποθήκευση</button><button type="button" onClick={() => setShowManualForm(false)} className="rounded-lg bg-gray-100 px-4 py-2 text-gray-700">Ακύρωση</button></div>
        </form>
      )}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div className="md:col-span-2"><label className="mb-1 block text-sm font-medium text-gray-700">Αναζήτηση</label><input value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Όνομα, email, τηλέφωνο, θέμα, project..." className="w-full rounded-lg border border-gray-300 px-3 py-2" /></div>
        <div><label className="mb-1 block text-sm font-medium text-gray-700">Κατάσταση</label><select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="w-full rounded-lg border border-gray-300 px-3 py-2"><option value="all">Όλες</option><option value="new">New</option><option value="contacted">Contacted</option><option value="converted">Converted</option></select></div>
      </div>

      <div className="rounded-xl bg-white shadow overflow-hidden">
        {loading ? <div className="p-10 text-center">Φόρτωση...</div> : filteredLeads.length === 0 ? <div className="p-10 text-center text-gray-500">Δεν βρέθηκαν leads.</div> : (
          <div className="overflow-x-auto"><table className="min-w-full divide-y divide-gray-200"><thead className="bg-gray-50"><tr><th className="px-4 py-3 text-left text-xs uppercase text-gray-500">Lead</th><th className="px-4 py-3 text-left text-xs uppercase text-gray-500">Contact</th><th className="px-4 py-3 text-left text-xs uppercase text-gray-500">Subject</th><th className="px-4 py-3 text-left text-xs uppercase text-gray-500">Status</th><th className="px-4 py-3 text-left text-xs uppercase text-gray-500">Client</th><th className="px-4 py-3 text-left text-xs uppercase text-gray-500">Project</th><th className="px-4 py-3 text-right text-xs uppercase text-gray-500">Actions</th></tr></thead><tbody className="divide-y divide-gray-100">{filteredLeads.map((lead) => (
            <tr key={lead.id}><td className="px-4 py-4"><div className="font-medium text-gray-900">{lead.name}</div><div className="text-xs text-gray-500">{lead.created_at ? new Date(lead.created_at).toLocaleString('el-GR') : '—'}</div></td><td className="px-4 py-4 text-sm"><div>{lead.email || '—'}</div><div>{lead.phone || '—'}</div></td><td className="px-4 py-4 text-sm">{lead.subject || '—'}</td><td className="px-4 py-4"><span className={`rounded-full px-2.5 py-1 text-xs font-medium ${statusBadgeClasses(lead.status)}`}>{lead.status || '—'}</span></td><td className="px-4 py-4 text-sm">{lead.client_name || '—'}</td><td className="px-4 py-4 text-sm">{lead.internal_project_title || '—'}</td><td className="px-4 py-4"><div className="flex flex-wrap justify-end gap-2">
              {!lead.internal_project_id ? <button onClick={() => createProject(lead)} disabled={submitting} className="rounded-lg bg-purple-50 px-3 py-2 text-sm text-purple-700 hover:bg-purple-100 disabled:opacity-50">Δημιουργία Project</button> : <button onClick={() => openProjectFinance(lead)} className="rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-700 hover:bg-emerald-100">Οικονομικά Project</button>}
              {!lead.client_id ? <button onClick={() => openClientConversion(lead)} className="rounded-lg bg-blue-50 px-3 py-2 text-sm text-blue-700 hover:bg-blue-100">Δημιουργία Πελάτη</button> : <button onClick={() => navigate('/admin/clients', { state: { openClientId: lead.client_id } })} className="rounded-lg bg-gray-100 px-3 py-2 text-sm text-gray-700 hover:bg-gray-200">Άνοιγμα Πελάτη</button>}
            </div></td></tr>
          ))}</tbody></table></div>
        )}
      </div>

      <Modal isOpen={showClientModal} onClose={() => setShowClientModal(false)} title={currentLead ? `Δημιουργία πελάτη από ${currentLead.name}` : 'Δημιουργία πελάτη'} size="lg">
        <form onSubmit={convertToClient} className="space-y-4">
          <input required placeholder="Όνομα πελάτη" value={clientForm.client_name} onChange={(e) => setClientForm((p) => ({ ...p, client_name: e.target.value }))} className="w-full rounded-lg border border-gray-300 px-3 py-2" />
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2"><input type="email" placeholder="Email" value={clientForm.email} onChange={(e) => setClientForm((p) => ({ ...p, email: e.target.value }))} className="rounded-lg border border-gray-300 px-3 py-2" /><input placeholder="Τηλέφωνο" value={clientForm.phone} onChange={(e) => setClientForm((p) => ({ ...p, phone: e.target.value }))} className="rounded-lg border border-gray-300 px-3 py-2" /></div>
          {!currentLead?.internal_project_id && <input placeholder="Τίτλος project" value={clientForm.project_title} onChange={(e) => setClientForm((p) => ({ ...p, project_title: e.target.value }))} className="w-full rounded-lg border border-gray-300 px-3 py-2" />}
          <div className="flex justify-end gap-2"><button type="button" onClick={() => setShowClientModal(false)} className="rounded-lg bg-gray-100 px-4 py-2">Ακύρωση</button><button type="submit" disabled={submitting} className="rounded-lg bg-blue-600 px-4 py-2 text-white disabled:opacity-50">Δημιουργία Πελάτη</button></div>
        </form>
      </Modal>
    </div>
  )
}